import React, { useMemo, useCallback } from 'react'
import CartReviewTile, { LoyaltyPointsEarningConfiguration } from './CartReviewTile'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import TileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import CartReviewPriceStack from './CartReviewPriceStack'
import CartReviewBanner from './CartReviewBanner'
import { getBannerTypeVariant } from './CartReviewUtils'
import useUserCartHotelItemData from 'hooks/UserCart/useUserCartHotelItemData'
import offerPageURL from 'lib/offer/offerPageURL'
import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import CartReviewTileImageContent from './CartReviewTileImageContent'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import { userCartItemsSaveForLater, userCartItemsDelete } from 'actions/AuthActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { queryKeyAdults, queryKeyCheckIn, queryKeyCheckOut, queryKeyChildren } from 'constants/url'
import { encodeChildrenOccupancy } from 'lib/search/searchUtils'
import { getUserCartAccommodationItems } from 'selectors/userCart'
import { multiCartDeleteItemEvent, multiCartSaveItemForLaterEvent } from 'analytics/snowplow/events'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import AccommodationTileComparisonCheckbox from './AccommodationTileComparisonCheckbox'

interface Props {
  item: App.Checkout.InstantBookingLEHotelItem
  className?: string
  hideTitleAndDates?: boolean
  hideAvailabilityDetails?: boolean
  disableOverlay?: boolean
  saveForLaterIfUnavailable?: boolean
  tileVariant?: 'default' | 'condensed'
}

function LeHotelCartReviewTile({ item, className, hideTitleAndDates = false, hideAvailabilityDetails = false, disableOverlay = false, saveForLaterIfUnavailable = true, tileVariant = 'default' }: Props) {
  const dispatch = useAppDispatch()
  const data = useUserCartHotelItemData(item, saveForLaterIfUnavailable)
  const isInstantBooking = isInstantBookingLEHotelItem(item)
  const hotelItems = useAppSelector(getUserCartAccommodationItems)

  // There's a bug where the package availableRooms can still show a value even when the package is sold out
  const roomsLeft = data.isSoldOut ? 0 : (data.package?.availableRooms ?? 0)
  const image = data.package?.roomType.images[0] || data.offer?.image

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartSaveItemForLaterEvent(item, data.isSavedForLater)
    dispatch(userCartItemsSaveForLater([item.itemId]))
  }, [dispatch, item, data.isSavedForLater])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    multiCartDeleteItemEvent(item)
    dispatch(userCartItemsDelete([item.itemId]))
  }, [dispatch, item])

  const {
    publicTotal,
    memberTotal,
  } = useMemo(() => getUserCartItemDisplayTotals(data.itemTotals), [data?.itemTotals])

  const formattedDateRange = useMemo(() => {
    if (isInstantBooking) {
      const dateRange = formatDynamicDateRange(item.checkIn, item.checkOut)
      if (!dateRange) return false
      return `${dateRange} · ${item.duration} Nights`
    }
    return false
  }, [isInstantBooking, item.checkIn, item.checkOut, item.duration])

  const formattedOccupancy = useMemo(() => {
    if (!item.occupancy) return false
    return formatOccupantsShort(item.occupancy)
  }, [item.occupancy])

  const bannerTypeVariant = getBannerTypeVariant(data.isPast, data.isSoldOut, data.isUnavailable, !!data.error)

  const offerLink = useMemo(() => {
    const params = new URLSearchParams()

    const itemGroup = hotelItems.filter(isInstantBookingLEHotelItem).filter(i => i.offerId === item.offerId && i.checkIn === item.checkIn && i.checkOut === item.checkOut)
    const rooms = itemGroup.map(i => i.occupancy)

    if (!data.isPast) {
      params.set(queryKeyCheckIn, item.checkIn)
      params.set(queryKeyCheckOut, item.checkOut)
    }

    params.set(queryKeyAdults, rooms.map(room => room.adults).join(','))
    params.set(queryKeyChildren, rooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(','))

    return offerPageURL(data.offer, params)
  }, [data.offer, hotelItems, item, data.isPast])
  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    if (!data.offer || !data.itemTotals) {
      return {
        calculationRequests: [],
        calculationType: 'exact',
      }
    }
    const calculationRequest = generateLoyaltyPointsCalculatorHotelOptions(data.offer, {
      pkg: data.package,
      price: publicTotal,
      memberPrice: memberTotal,
    })

    return {
      calculationRequests: [calculationRequest],
      calculationType: 'exact',
    }
  }, [data.offer, data.package, data.itemTotals, publicTotal, memberTotal])

  const priceTotals = useMemo(() => ({
    price: publicTotal,
    memberPrice: memberTotal,
    value: data.itemTotals?.value ?? 0,
  }), [data.itemTotals?.value, memberTotal, publicTotal])

  const filters = useMemo(() => ({
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    rooms: [item.occupancy],
  }), [item.checkIn, item.checkOut, item.occupancy])

  return (
    <CartReviewTile
      itemId={item.itemId}
      fetching={data.loading}
      label={roomsLeft ? <RoomsRemainingLabel roomCount={roomsLeft} threshold="rate" /> : undefined}
      to={offerLink}
      imageElement={<CartReviewTileImageContent image={image} />}
      title={data.offer?.property.name || data.offer?.name || ''}
      dates={formattedDateRange}
      packageTitle={data.package?.name}
      occupancy={formattedOccupancy}
      loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
      cancellationPolicy={data.offer && data.package?.roomRate.cancellationPolicy.type && (
        <TileCancellationPolicy
          cancellationPolicyType={data.package.roomRate.cancellationPolicy.type}
          checkInDate={isInstantBooking ? item.checkIn : ''}
          timezoneOffset={data.offer.property.timezoneOffset || 0}
          offerType={data.offer.type}
          textSize="medium"
        />
      )}
      price={
        <CartReviewPriceStack
          price={publicTotal}
          memberPrice={memberTotal}
          value={data.itemTotals?.value ?? 0}
          luxPlusOfferConfig={data.offer?.luxPlus || {}}
        />
      }
      banner={
        <CartReviewBanner
          variant={bannerTypeVariant}
          link={offerLink}
        />
      }
      isBookable={data.isBookable}
      saveForLater={data.isSavedForLater}
      deleted={data.isDeleted}
      onSaveForLater={handleSaveForLater}
      onDelete={handleDelete}
      className={className}
      hideAvailabilityDetails={hideAvailabilityDetails}
      hideTitleAndDates={hideTitleAndDates}
      disableOverlay={disableOverlay}
      roomName={data.package?.roomType.name}
      tileVariant={tileVariant}
      comparisonCheckbox={<AccommodationTileComparisonCheckbox
        cartItemId={item.itemId}
        offerType="hotel"
        offerId={data.offer?.id || ''}
        name={data.offer?.name || ''}
        packageId={data.package?.id || ''}
        filters={filters}
        priceTotals={priceTotals}
      />}
    />
  )
}

export default LeHotelCartReviewTile
